import React, { useState } from 'react';
import mixpanel from 'mixpanel-browser';

interface ShareActionsProps {
  postcardBlob: Blob | null;
  shareUrl: string;
  sessionCount: number;
  worldName: string;
}

const ShareActions: React.FC<ShareActionsProps> = ({
  postcardBlob,
  shareUrl,
  sessionCount,
  worldName,
}) => {
  const [copied, setCopied] = useState(false);

  const handleDownload = () => {
    if (!postcardBlob) return;

    mixpanel.track('Download Postcard', { world: worldName });

    const url = URL.createObjectURL(postcardBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kaizenflow-${worldName.toLowerCase().replace(/\s+/g, '-')}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCopyLink = async () => {
    mixpanel.track('Copy Share Link', { world: worldName });

    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleShareTwitter = () => {
    mixpanel.track('Share Twitter', { world: worldName });

    const text = sessionCount === 1
      ? `Just completed my first deep work session in ${worldName}! ⚡`
      : `${sessionCount} deep work sessions completed today in ${worldName}! ⚡`;

    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}&via=kaizen_flow`;
    window.open(twitterUrl, '_blank', 'width=550,height=420');
  };

  const handleWebShare = async () => {
    if (!navigator.share) return;

    mixpanel.track('Web Share', { world: worldName });

    const shareData: ShareData = {
      title: 'Kaizen Flow - Session Complete',
      text: `I just completed a deep work session in ${worldName}!`,
      url: shareUrl,
    };

    // Add image if available and supported
    if (postcardBlob && navigator.canShare?.({ files: [new File([postcardBlob], 'postcard.png', { type: 'image/png' })] })) {
      shareData.files = [new File([postcardBlob], 'kaizenflow-session.png', { type: 'image/png' })];
    }

    try {
      await navigator.share(shareData);
    } catch (err) {
      // User cancelled or share failed
    }
  };

  const supportsWebShare = typeof navigator.share === 'function';

  return (
    <div className="flex flex-wrap items-center justify-center gap-3">
      {/* Download postcard */}
      <button
        onClick={handleDownload}
        disabled={!postcardBlob}
        className="btn-subtle flex items-center gap-2"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
        Download
      </button>

      {/* Copy link */}
      <button
        onClick={handleCopyLink}
        className="btn-subtle flex items-center gap-2"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          {copied ? (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
          )}
        </svg>
        {copied ? 'Copied!' : 'Copy link'}
      </button>

      {/* Share to Twitter */}
      <button
        onClick={handleShareTwitter}
        className="btn-subtle flex items-center gap-2"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
        Tweet
      </button>

      {/* Web Share API (mobile) */}
      {supportsWebShare && (
        <button
          onClick={handleWebShare}
          className="btn-subtle flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
          </svg>
          Share
        </button>
      )}
    </div>
  );
};

export default ShareActions;
